import { FC, useEffect, useState } from "react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";
import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";

export interface NcInputNumberProps {
  className?: string;
  defaultValue?: number;
  min?: number;
  max?: number;
  onChange?: (value: number) => void;
  label?: string;
  desc?: string;
}

const NcInputNumber: FC<NcInputNumberProps> = ({
  defaultValue = 1,
  min = 0,
  max,
}) => {
  const { setGuests, guests } = useHotels();

  const [agesChildren, setAgesChildren] = useState<number[]>([]);
  const [agesError, setAgesError] = useState<boolean[]>([]);

  useEffect(() => {
    if ((guests.agesChildren?.length ?? 0) > 0 && agesChildren.length === 0) {
      setAgesChildren(guests.agesChildren ?? []);
    }

    setGuests &&
      setGuests({
        adults: guests.adults,
        children: guests.children,
        agesChildren: agesChildren,
      });
  }, [
    defaultValue,
    setGuests,
    guests.adults,
    guests.children,
    guests.agesChildren,
  ]);

  const updateGuests = (
    adults: number,
    children: number,
    agesChildren?: number[]
  ) => {
    setGuests({
      adults: adults,
      children: children,
      agesChildren: agesChildren,
    });
  };

  const handleClickDecrementAdults = () => {
    updateGuests(Math.max(1, guests.adults - 1), guests.children);
  };

  const handleClickIncrementAdults = () => {
    updateGuests(Math.min(8, guests.adults + 1), guests.children);
  };

  const handleClickDecrementChildren = () => {
    updateGuests(guests.adults, Math.max(0, guests.children - 1));

    setAgesChildren(agesChildren.slice(0, -1));
  };

  const handleClickIncrementChildren = () => {
    if (max && max <= guests.children) return;
    updateGuests(guests.adults, Math.min(3, guests.children + 1));
  };

  const handleAgesChildren = (index: number, value: number) => {
    if (value < 3 || value > 12) {
      agesError[index] = true;
    }

    if (value >= 3 && value <= 12) {
      agesError[index] = false;
    }

    const newAges = [...agesChildren];

    newAges[index] = value;

    setAgesChildren(newAges);

    if (newAges.length === 0) {
      return updateGuests(guests.adults, guests.children);
    }

    updateGuests(guests.adults, guests.children, newAges);
  };

  return (
    <div>
      <div
        className={`nc-NcInputNumber flex items-center justify-between space-x-5`}
      >
        <div className="flex flex-col">
          <span className="font-medium text-neutral-800 dark:text-neutral-200">
            Adultos
          </span>

          <span className="text-xs text-neutral-500 dark:text-neutral-400 font-normal">
            13 anos ou mais
          </span>
        </div>

        <div
          className={`nc-NcInputNumber flex items-center justify-between w-28`}
        >
          <button
            className="w-8 h-8 rounded-full flex items-center justify-center border border-neutral-400 dark:border-neutral-500 bg-white dark:bg-neutral-900 focus:outline-none hover:border-neutral-700 disabled:hover:border-neutral-400 dark:disabled:hover:border-neutral-500 disabled:opacity-50 disabled:cursor-default"
            type="button"
            onClick={handleClickDecrementAdults}
            disabled={min >= guests.adults}
          >
            <MinusIcon className="w-4 h-4" />
          </button>
          <span>{guests.adults} </span>
          <button
            className="w-8 h-8 rounded-full flex items-center justify-center border border-neutral-400 dark:border-neutral-500 bg-white dark:bg-neutral-900 focus:outline-none hover:border-neutral-700 disabled:hover:border-neutral-400 dark:disabled:hover:border-neutral-500 disabled:opacity-50 disabled:cursor-default"
            type="button"
            onClick={handleClickIncrementAdults}
            disabled={max ? max <= guests.adults : false}
          >
            <PlusIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        className={`nc-NcInputNumber flex items-center justify-between space-x-5 mt-5`}
      >
        <div className="flex flex-col">
          <span className="font-medium text-neutral-800 dark:text-neutral-200">
            Crianças
          </span>

          <span className="text-xs text-neutral-500 dark:text-neutral-400 font-normal">
            De 3 a 12 anos
          </span>
        </div>

        <div
          className={`nc-NcInputNumber flex items-center justify-between w-28`}
        >
          <button
            className="w-8 h-8 rounded-full flex items-center justify-center border border-neutral-400 dark:border-neutral-500 bg-white dark:bg-neutral-900 focus:outline-none hover:border-neutral-700 disabled:hover:border-neutral-400 dark:disabled:hover:border-neutral-500 disabled:opacity-50 disabled:cursor-default"
            type="button"
            onClick={handleClickDecrementChildren}
            disabled={min >= guests.children!}
          >
            <MinusIcon className="w-4 h-4" />
          </button>
          <span>{guests.children! ?? 0} </span>
          <button
            className="w-8 h-8 rounded-full flex items-center justify-center border border-neutral-400 dark:border-neutral-500 bg-white dark:bg-neutral-900 focus:outline-none hover:border-neutral-700 disabled:hover:border-neutral-400 dark:disabled:hover:border-neutral-500 disabled:opacity-50 disabled:cursor-default"
            type="button"
            onClick={handleClickIncrementChildren}
            disabled={max ? max <= guests.children! : false}
          >
            <PlusIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {guests.children > 0 ? (
        <div className={`w-full flex-col items-center justify-between mt-5`}>
          <span className="font-medium text-neutral-800 dark:text-neutral-200 underline">
            Idades
          </span>

          <div className="w-full flex-col space-y-5 relative">
            {Array.from({ length: guests.children }).map((_, index) => (
              <div
                key={`${index}-children-ages`}
                className={`flex items-center justify-between w-full pr-6`}
              >
                <span className="text-base text-neutral-500 dark:text-neutral-400 font-normal">
                  {index + 1}ª Criança
                </span>
                <div className="relative flex">
                  <input
                    key={index}
                    type="text"
                    onBlur={
                      agesChildren[index] &&
                      (agesChildren[index] < 3 || agesChildren[index] > 12)
                        ? () => setAgesError([...agesError, true])
                        : () => setAgesError([...agesError, false])
                    }
                    value={guests.agesChildren?.[index] || ""}
                    onChange={(e) => {
                      handleAgesChildren(index, Number(e.target.value));
                    }}
                    className="w-16 h-8 rounded-md border border-neutral-400 dark:border-neutral-500 focus:outline-none"
                  />

                  {agesError[index] ? (
                    <>
                      <div
                        className="hidden lg:flex absolute left-20 -top-1 items-center justify-center bg-red-500 rounded-md transition 
                  "
                      >
                        <span className="relative block -left-1.5 z-10 bg-red-500  w-5 h-5 transform rotate-45" />
                        <span className="w-28 z-30 p-1 relative  text-xs  text-white">
                          A idade deve ser entre 3 e 12 anos
                        </span>
                      </div>

                      <div className="lg:hidden absolute -left-32 top-1 bg-red-500 items-center justify-center rounded-md transition">
                        <span className="w-28 p-1 relative z-30 text-xs  text-white">
                          Entre 3 e 12 anos
                        </span>
                        <span className="absolute z-10 bg-red-500 -right-1 top-1 w-4 h-4 transform rotate-45 block" />
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NcInputNumber;
